import { useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiArrowLeftRightLine, RiBuilding2Line } from '@remixicon/react'
import type { WorkspaceListItem } from 'shared'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui'
import { IDENTITY_BADGE, monogram } from '../../lib/identity'
import { cx, focusRing } from '../../lib/utils'

interface WorkspaceSwitcherProps {
  workspaces: WorkspaceListItem[]
  /**
   * Asks the caller to refetch. The list is loaded once by the layout, so a
   * company created a moment ago is not in it yet — and the switcher would
   * rather ask again than name the wrong company.
   */
  onStale: () => void
}

/**
 * Which company you are in, and the way to any other.
 *
 * The console's whole job is per-company work, but navigation used to treat a
 * company as a leaf: the only route from one to the next was back out to the
 * list and in again. This puts that move where the answer to "where am I"
 * already is, and keeps the company's own colour on it so the switch reads
 * before it is read.
 */
export default function WorkspaceSwitcher({ workspaces, onStale }: WorkspaceSwitcherProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { workspaceId } = useParams()

  const current = useMemo(
    () => workspaces.find((workspace) => workspace.id === workspaceId) ?? null,
    [workspaces, workspaceId],
  )

  /**
   * Only the active ones are worth offering. The lookup above still searches
   * all of them, so being inside an archived company shows its name rather
   * than claiming you are nowhere in particular.
   */
  const switchable = useMemo(
    () => workspaces.filter((workspace) => workspace.status !== 'archived'),
    [workspaces],
  )

  // Ask for a refetch once per unknown company, never in a loop: an id that
  // is simply wrong stays unknown however many times we look.
  const asked = useRef<string | null>(null)
  useEffect(() => {
    if (!workspaceId || current || asked.current === workspaceId) return
    asked.current = workspaceId
    onStale()
  }, [workspaceId, current, onStale])

  // Nothing to switch between, and nothing to say about where you are.
  if (workspaces.length === 0) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('nav.switchWorkspace')}
          className={cx(
            'flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition',
            'hover:bg-gray-200/60 dark:hover:bg-gray-900',
            focusRing,
          )}
        >
          <span
            aria-hidden="true"
            className={cx(
              'flex size-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ring-1',
              current ? IDENTITY_BADGE : 'bg-gray-100 text-gray-500 ring-gray-200 dark:bg-gray-900 dark:text-gray-400 dark:ring-gray-800',
            )}
          >
            {current ? monogram(current.name) : <RiBuilding2Line className="size-4" />}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
              {current ? current.name : t('nav.allCompanies')}
            </span>
            <span className="truncate text-xs text-gray-500 dark:text-gray-400">
              {t('nav.switchWorkspace')}
            </span>
          </span>
          <RiArrowLeftRightLine
            className="size-4 shrink-0 text-gray-400 dark:text-gray-600"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
        <DropdownMenuItem onClick={() => navigate('/app/workspaces')}>
          <span className="flex items-center gap-2.5">
            <RiBuilding2Line className="size-4 text-gray-400" aria-hidden="true" />
            {t('nav.allCompanies')}
          </span>
        </DropdownMenuItem>

        {switchable.map((workspace) => (
          <DropdownMenuItem
            key={workspace.id}
            onClick={() => navigate(`/app/workspaces/${workspace.id}`)}
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <span
                aria-hidden="true"
                className={cx(
                  'flex size-5 shrink-0 items-center justify-center rounded text-[10px] font-bold ring-1',
                  IDENTITY_BADGE,
                )}
              >
                {monogram(workspace.name)}
              </span>
              <span className="truncate">{workspace.name}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
